import { useEffect, useRef, useState } from "react";
import { searchUsers, getUser } from "../../api/UserApi";
import Avatar from "../../components/Avatar";
import { SelectField } from "../../components/ui";

export type UserOption = {
  value: string;
  label: string;
  avatarUrl?: string;
  accountId?: string;
};

interface SelectUserProps {
  onChange: (opt: UserOption | null) => void;
  userId?: string;
  label?: string;
  isDisabled?: boolean;
}

/**
 * SelectUser
 * - Reuses SelectField UI (react-select under the hood)
 * - Async load options on input change (debounced)
 * - Prefills value when userId provided
 * - Emits full option via onChange (upstream reads option.value)
 */
const SelectUser = ({ onChange, userId, label, isDisabled }: SelectUserProps) => {
  const [user, setUser] = useState<UserOption | null>(null);
  const [options, setOptions] = useState<UserOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<any>(null);

  const toOption = (u: any): UserOption => ({
    value: u.accountId,
    label: u.displayName,
    avatarUrl: u.avatarUrl,
    accountId: u.accountId,
  });

  const fetchOptions = async (query: string) => {
    setIsLoading(true);
    try {
      const list = await searchUsers(query);
      setOptions(list.map(toOption));
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
    }
  };

  // Prefill initial user if userId provided
  useEffect(() => {
    const init = async () => {
      if (!userId) return;
      try {
        const { data } = await getUser(userId);
        const opt = toOption(data);
        setUser(opt);
        // make sure selected option is present in options list
        setOptions((prev) => (prev.find((p) => p.value === opt.value) ? prev : [opt, ...prev]));
      } catch {
        // ignore
      }
    };
    init();
  }, [userId]);

  // Initial options
  useEffect(() => {
    fetchOptions("");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onInputChange = (val: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(val.trim()), 350);
  };

  const formatOptionLabel = (option: UserOption) => (
    <div className="flex items-center gap-2" key={option.value}>
      <Avatar avatarUrl={option.avatarUrl} />
      <span>{option.label}</span>
    </div>
  );

  return (
    <SelectField<UserOption, false>
      placeholder="Search user"
      label={label}
      options={options}
      value={user as any}
      onChange={(opt: any) => {
        const next = (opt as UserOption) ?? null;
        setUser(next);
        onChange(next);
      }}
      onInputChange={onInputChange as any}
      formatOptionLabel={formatOptionLabel as any}
      isClearable
      isDisabled={isDisabled}
      isLoading={isLoading}
      menuPortalTarget={typeof window !== "undefined" ? document.body : undefined}
      className="w-full"
    />
  );
};

export default SelectUser;
